import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ClipboardPaste, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";


const LEGACY_ORDER_SUFFIX = "c9ab2d31";
const LEGACY_QUOTE_ID = "d4c48e95-9e8f-4a00-9e2d-bd8de13b227f";

export const isLegacyManualTarget = order => (
  String(order?.order_id || "").toLowerCase().endsWith(LEGACY_ORDER_SUFFIX)
  && String(order?.quote_id || "").toLowerCase() === LEGACY_QUOTE_ID
);

export const LegacyManualSizeImport = ({ order, onImported }) => {
  const [open, setOpen] = useState(false);
  const [rawText, setRawText] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const imported = order?.manual_size_import?.status === "imported";

  useEffect(() => {
    if (!open || !rawText.trim()) {
      setPreview(null);
      setPreviewing(false);
      return undefined;
    }
    setPreviewing(true);
    const timeout = window.setTimeout(async () => {
      try {
        const response = await axios.post(`${API}/orders/${order.order_id}/manual-size-override/preview`, { raw_text: rawText });
        setPreview(response.data);
      } catch (error) {
        setPreview(null);
        toast.error(error.response?.data?.detail || "Could not preview the pasted sizes");
      } finally {
        setPreviewing(false);
      }
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [open, order?.order_id, rawText]);

  const save = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/orders/${order.order_id}/manual-size-override/import`, { raw_text: rawText });
      toast.success(`${response.data.manual_size_import.valid_count} legacy size rows imported`);
      onImported?.(response.data);
      setOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.preview) setPreview(detail.preview);
      toast.error(detail?.message || detail || "Could not import the manual sizes");
    } finally {
      setSaving(false);
    }
  };

  return <>
    <Button
      variant="outline"
      size="sm"
      disabled={imported}
      onClick={() => setOpen(true)}
      className={imported ? "border-emerald-300 bg-emerald-50 text-emerald-800 disabled:opacity-100" : "border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"}
      data-testid="legacy-manual-size-override-button"
    >
      {imported ? "✓ Manual Sizes Imported" : <><ClipboardPaste className="mr-1 h-4 w-4" />Manual Size Override Input</>}
    </Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="legacy-manual-size-import-modal">
        <DialogHeader>
          <DialogTitle>Manual legacy size import</DialogTitle>
          <DialogDescription>Paste the email table. The Sq Ft column is ignored and recalculated by the production engine.</DialogDescription>
        </DialogHeader>
        <Textarea
          rows={9}
          value={rawText}
          onChange={event => setRawText(event.target.value)}
          placeholder={"Width (in)\tHeight (in)\tSq Ft\tQuantity\tType\n15-5/8\t24\t2.60\t2\tDoor\n12\t7\t0.58\t3\tDrawer Front"}
          className="font-mono text-sm"
          data-testid="legacy-manual-size-textarea"
        />
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="legacy-manual-size-preview-summary">
          {previewing && <span className="flex items-center text-slate-500"><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Parsing…</span>}
          {preview && <><Badge className="bg-emerald-100 text-emerald-800">{preview.valid_count} ready</Badge><Badge className={preview.invalid_count ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-600"}>{preview.invalid_count} need attention</Badge></>}
        </div>
        {preview?.lines?.length > 0 && <div className="overflow-hidden border border-slate-200" data-testid="legacy-manual-size-preview">
          <div className="grid grid-cols-[42px_minmax(0,1fr)] gap-2 bg-slate-950 px-3 py-2 text-xs font-semibold text-white sm:grid-cols-[42px_1fr_1fr_80px_130px]">
            <span>Line</span><span>Width</span><span className="hidden sm:block">Height</span><span className="hidden sm:block">Qty</span><span className="hidden sm:block">Type</span>
          </div>
          <div className="divide-y divide-slate-200">{preview.lines.map(line => (
            <div key={`${line.line_number}-${line.raw}`} className={`grid grid-cols-[42px_minmax(0,1fr)] gap-2 px-3 py-2 text-sm sm:grid-cols-[42px_1fr_1fr_80px_130px] ${line.status === "invalid" ? "bg-amber-100 text-amber-950" : line.status === "header" ? "bg-slate-50 text-slate-500" : "bg-white text-slate-800"}`} data-testid={`legacy-preview-line-${line.line_number}`}>
              <span className="font-mono text-xs">{line.line_number}</span>
              {line.status === "valid" ? <><span className="font-mono">{line.width}<span className="sm:hidden"> × {line.height} · {line.quantity} × {line.type}</span></span><span className="hidden font-mono sm:block">{line.height}</span><span className="hidden sm:block">{line.quantity}</span><span className="hidden sm:block">{line.type}</span></> : <div className="sm:col-span-4"><p className="font-mono text-xs break-all">{line.raw}</p>{line.error && <p className="mt-1 flex items-center text-xs font-semibold"><AlertTriangle className="mr-1 h-3.5 w-3.5" />{line.error}</p>}</div>}
            </div>
          ))}</div>
        </div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="legacy-manual-size-cancel-button">Cancel</Button>
          <Button className="bg-amber-600 text-white hover:bg-amber-700" onClick={save} disabled={saving || previewing || !preview?.can_import} data-testid="legacy-manual-size-save-button">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing…</> : <><Save className="mr-2 h-4 w-4" />Create Production Sheet</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
};